import { useAuth0 } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { Centered, PrimaryButton, SecondaryButton } from "../components/Centered";
import { pendingInvite } from "../lib/invite";
import { SessionGate, type Blocked } from "../lib/session";

/** The Auth0 connections a person can pick (OD-32): passwordless email code, or Google. */
type SignInConnection = "email" | "google-oauth2";

/** Everything between "opened the app" and "inside the app". */
export function Gate({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, error, loginWithRedirect, logout, user } = useAuth0();
  const signOut = () => logout({ logoutParams: { returnTo: window.location.origin } });
  // Each button names its Auth0 connection. Auth0's New Universal Login never offers the
  // passwordless email connection on its own (without `connection=email` its email field only does
  // enterprise home-realm discovery), so the choice is made here, not on Auth0's page. Come back
  // to the page they were on (onRedirectCallback in main.tsx navigates to returnTo).
  const signIn = (connection: SignInConnection) =>
    void loginWithRedirect({
      authorizationParams: { connection },
      appState: { returnTo: window.location.pathname + window.location.search },
    });
  const buttons = (
    <div className="flex flex-col gap-3 sm:flex-row">
      <PrimaryButton onClick={() => signIn("email")}>Email me a sign-in code</PrimaryButton>
      <SecondaryButton onClick={() => signIn("google-oauth2")}>Continue with Google</SecondaryButton>
    </div>
  );

  if (isLoading) return <Centered title="Signing in…"><p>One moment.</p></Centered>;

  if (!isAuthenticated) {
    const invited = pendingInvite() !== null;
    return (
      <Centered title={invited ? "You've been invited" : "Sign in"}>
        {error && <p className="text-bad">Sign-in didn't complete. Try again.</p>}
        <p>
          {invited
            ? "Sign in as the address the invitation was sent to: with Google if it's a Google account, otherwise with a one-time code we email to it."
            : "HypheSync is invite-only. Sign in as the address your admin invited: with Google if it's a Google account, otherwise with a one-time code we email to it."}
        </p>
        {buttons}
      </Centered>
    );
  }

  return (
    <SessionGate
      renderLoading={() => <Centered title="Loading your workspace…"><p>One moment.</p></Centered>}
      renderExpired={() => (
        <Centered title="Your session has ended">
          <p>Sign in again to continue.</p>
          {buttons}
        </Centered>
      )}
      renderError={(retry) => (
        <Centered title="Couldn't reach HypheSync">
          <p>Check your connection and try again.</p>
          <div className="flex flex-wrap gap-3">
            <PrimaryButton onClick={retry}>Try again</PrimaryButton>
            <SecondaryButton onClick={signOut}>Sign out</SecondaryButton>
          </div>
        </Centered>
      )}
      renderBlocked={(reason) => (
        <Centered title={BLOCKED_TITLE[reason]}>
          <p>{blockedText(reason, user?.email)}</p>
          <div>
            <SecondaryButton onClick={signOut}>Sign out</SecondaryButton>
          </div>
        </Centered>
      )}
    >
      {children}
    </SessionGate>
  );
}

const BLOCKED_TITLE: Record<Blocked, string> = {
  no_access: "No access yet",
  disabled: "Account disabled",
  tenant_suspended: "Workspace suspended",
  org_mismatch: "Wrong organization",
  email_missing: "No email on this sign-in",
  email_unverified: "Email not verified",
  email_mismatch: "Different account",
  invitation_expired: "Invitation no longer valid",
  invitation_invalid: "Invitation not found",
  already_member: "Already a member elsewhere",
  already_accepted: "Invitation already accepted",
};

function blockedText(reason: Blocked, email: string | undefined): string {
  const as = email ? ` as ${email}` : "";
  switch (reason) {
    case "no_access": return `You're signed in${as}, but this account hasn't been invited. Ask your admin to send you an invitation.`;
    case "disabled": return "An admin has disabled this account. Contact your admin if that's unexpected.";
    case "tenant_suspended": return "This workspace is suspended. Contact HypheSync support.";
    case "org_mismatch": return "You signed in through an organization this workspace doesn't use. Sign out and sign in again.";
    case "email_missing": return "Your sign-in didn't include an email address, so the invitation can't be matched to you. This is a HypheSync setup problem: tell your admin, then try again.";
    case "email_unverified": return `Your sign-in provider hasn't verified the email${as ? " on this account" : ""}. Verify it with that provider, or sign in with the emailed one-time code instead.`;
    case "email_mismatch": return `You're signed in${as}, but the invitation was sent to a different address. Sign out and sign in with that account.`;
    case "invitation_expired": return "This invitation has expired, was replaced by a newer one, or was already used. Ask your admin to send a new one.";
    case "invitation_invalid": return "This invitation link isn't valid. Ask your admin to send a new one.";
    case "already_member": return `This account${as} already belongs to another HypheSync workspace.`;
    case "already_accepted": return "This invitation was already accepted with this account. Sign out and back in to open your workspace.";
  }
}
